import { NavLink } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Command, PanelLeftClose, TrendingDown, TrendingUp, X,
} from "lucide-react";
import { Tooltip } from "../ui/Tooltip";
import { visibleGroups } from "./navigation";
import { useFlash } from "../../hooks/useFlash";

function NavItem({ item, isCollapsed, badge, onNavigate }) {
  const Icon = item.icon;

  const link = (
    <NavLink
      to={item.path}
      end  // /rules must not stay lit on /rules/tutorial
      onClick={onNavigate}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-150 ${
          isCollapsed ? "lg:justify-center lg:px-0" : ""
        } ${
          isActive
            ? "bg-gradient-to-r from-cyan-500/[.2] via-cyan-500/[.08] to-transparent text-cyan-300 shadow-[inset_0_0_0_1px_rgb(var(--c-cyan-500)/.18)]"
            : "text-low hover:bg-slate-700/35 hover:text-hi"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active marker rides the left edge of the rail. */}
          <span
            className={`absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-cyan-400 shadow-[0_0_12px_1px_rgb(var(--c-cyan-400)/.9)] transition-all duration-200 ${
              isActive ? "opacity-100" : "scale-y-0 opacity-0"
            }`}
          />
          <Icon className={`h-[18px] w-[18px] shrink-0 transition-transform duration-200 ${isActive ? "" : "group-hover:scale-110"}`} />
          <span className={`min-w-0 flex-1 truncate ${isCollapsed ? "lg:hidden" : ""}`}>{item.label}</span>
          {badge > 0 && (
            <span
              className={`flex h-5 min-w-[20px] items-center justify-center rounded-full bg-cyan-400 px-1 text-[10px] font-black text-on-dark ${
                isCollapsed ? "lg:absolute lg:right-1.5 lg:top-1.5 lg:h-4 lg:min-w-[16px]" : ""
              }`}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  // Collapsed rail relies on tooltips; expanded rail shows the description on hover.
  return (
    <Tooltip
      content={isCollapsed ? item.label : item.description}
      position="right"
      maxWidth={260}
      triggerClassName="block w-full"
    >
      {link}
    </Tooltip>
  );
}

export function Sidebar({
  isCollapsed,
  onToggle,
  mobileOpen = false,
  onMobileClose,
  openTradeCount = 0,
  todayPnl = 0,
  traderActive = false,
  traderMode = "PAPER",
  role = "admin",
  onOpenCommand,
}) {
  const groups = visibleGroups(role);
  const pnlUp = todayPnl >= 0;
  const PnlIcon = pnlUp ? TrendingUp : TrendingDown;
  const pnlFlash = useFlash(Math.round(todayPnl));

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onMobileClose}
          className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-line bg-surface/95 backdrop-blur-xl transition-[transform,width] duration-300 ease-swift lg:z-40 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 ${isCollapsed ? "lg:w-[76px]" : "lg:w-64"}`}
      >
        {/* Brand */}
        <div className={`flex h-16 shrink-0 items-center gap-3 border-b border-line px-4 ${isCollapsed ? "lg:justify-center lg:px-0" : ""}`}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 text-base font-black text-white shadow-[0_10px_28px_-10px_rgb(var(--c-cyan-500)/.9)]">
            ∞
          </div>
          <div className={`min-w-0 flex-1 ${isCollapsed ? "lg:hidden" : ""}`}>
            <span className="block truncate text-[15px] font-bold tracking-tight text-hi">Infinity</span>
            <span className="block text-[10px] font-bold uppercase tracking-eyebrow text-low">Options intelligence</span>
          </div>
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Close navigation"
            className="rounded-lg p-2 text-low hover:bg-slate-700/40 hover:text-hi lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onToggle}
            aria-label="Collapse navigation"
            className={`hidden rounded-lg p-2 text-low transition-colors hover:bg-slate-700/40 hover:text-hi lg:block ${isCollapsed ? "lg:hidden" : ""}`}
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        {/* Search / command palette entry */}
        <div className={`px-3 pt-3 ${isCollapsed ? "lg:px-2" : ""}`}>
          <button
            type="button"
            onClick={onOpenCommand}
            className={`flex w-full items-center gap-2 rounded-xl border border-line bg-surface-2 px-3 py-2 text-xs text-low transition-colors hover:border-cyan-500/40 hover:text-hi ${
              isCollapsed ? "lg:justify-center lg:px-0" : ""
            }`}
          >
            <Command className="h-3.5 w-3.5 shrink-0" />
            <span className={`flex-1 text-left ${isCollapsed ? "lg:hidden" : ""}`}>Jump to…</span>
            <kbd className={`rounded border border-line-strong bg-surface-3 px-1.5 py-0.5 font-mono text-[10px] text-low ${isCollapsed ? "lg:hidden" : ""}`}>
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {groups.map((group) => (
            <div key={group.id} className="space-y-1">
              <p className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-eyebrow text-faint ${isCollapsed ? "lg:hidden" : ""}`}>
                {group.label}
              </p>
              {isCollapsed && <div className="mx-auto hidden h-px w-6 bg-line lg:block" />}
              {group.items.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  isCollapsed={isCollapsed}
                  badge={item.path === "/autotrader" ? openTradeCount : 0}
                  onNavigate={onMobileClose}
                />
              ))}
            </div>
          ))}
        </nav>

        {/* Session summary */}
        <div className="shrink-0 space-y-2 border-t border-line p-3">
          <div
            className={`rounded-2xl border px-3 py-2.5 ${
              traderActive
                ? "border-emerald-500/25 bg-emerald-500/[.08]"
                : "border-line bg-surface-2"
            }`}
          >
            <div className={`flex items-center gap-2.5 ${isCollapsed ? "lg:justify-center" : ""}`}>
              <span className="relative flex h-2 w-2 shrink-0">
                {traderActive && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />}
                <span className={`relative inline-flex h-2 w-2 rounded-full ${traderActive ? "bg-emerald-400" : "bg-slate-600"}`} />
              </span>
              <div className={`min-w-0 flex-1 ${isCollapsed ? "lg:hidden" : ""}`}>
                <span className={`block text-[10px] font-bold uppercase tracking-label ${traderActive ? "text-emerald-300" : "text-low"}`}>
                  {traderActive ? "Autopilot active" : "Autopilot stopped"}
                </span>
                <span className="mt-0.5 block text-[10px] text-faint">{traderMode} execution</span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-surface-2 px-3 py-2.5">
            <div className={`flex items-center gap-2.5 ${isCollapsed ? "lg:justify-center" : ""}`}>
              <PnlIcon className={`h-4 w-4 shrink-0 ${pnlUp ? "text-emerald-400" : "text-rose-400"}`} />
              <div className={`min-w-0 flex-1 ${isCollapsed ? "lg:hidden" : ""}`}>
                <span className="block text-[10px] font-bold uppercase tracking-eyebrow text-faint" title={`Realised + unrealised today of every strategy (rules + manual), net of charges — ${traderMode} mode rows only`}>Today&apos;s P&amp;L · all books</span>
                <span className={`mt-0.5 block text-lg font-bold tabular-nums ${pnlUp ? "text-emerald-400" : "text-rose-400"} ${pnlFlash}`}>
                  {pnlUp ? "+" : "−"}₹{Math.abs(todayPnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onToggle}
            aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
            className="hidden w-full items-center justify-center gap-2 rounded-xl border border-line bg-surface-2 py-2 text-xs font-semibold text-low transition-colors hover:border-cyan-500/40 hover:text-hi lg:flex"
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /> Collapse</>}
          </button>
        </div>
      </aside>
    </>
  );
}
